import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Alert,
  Autocomplete,
  TextField,
  Stack,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress
} from "@mui/material";
import {
  Person as PersonIcon,
  Clear as ClearIcon,
  PlaylistAdd as PlaylistAddIcon
} from "@mui/icons-material";
import { GetAllCustomers, GetBulkRecipients } from "../../api/customers";
import { getAllCustomerGroups } from "../../api/customerGroup";
import { GetFinalStationList } from "../../api/stations";
import { BG_PURPLE_TINT } from "components/bento/bentoTokens";

const ACTIVITY_PERIOD_OPTIONS = ["1m", "3m", "6m", "1y", "never"];

const CustomerAutocomplete = ({
  selectedCustomers,
  setSelectedCustomers
}) => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const [inputValue, setInputValue] = useState("");

  // Station + last-activity filters (used by "Add All Filtered", resolved server-side)
  const [stationOptions, setStationOptions] = useState([]);
  const [selectedStations, setSelectedStations] = useState([]);
  const [activityMode, setActivityMode] = useState("active");
  const [activityPeriod, setActivityPeriod] = useState("");
  const [filteredRecipients, setFilteredRecipients] = useState(null);
  const [countLoading, setCountLoading] = useState(false);

  // Search within the already-selected list, to find one customer to remove
  // without scrolling/scanning through a long chip list.
  const [selectedSearchTerm, setSelectedSearchTerm] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const [customersResponse, groupsResponse, stationsResponse] = await Promise.all([
        GetAllCustomers(), // Use GetAllCustomers to fetch ALL customers without pagination
        getAllCustomerGroups(),
        GetFinalStationList()
      ]);

      if (customersResponse.success) {
        console.log("Customers loaded:", customersResponse.data?.length || 0, "customers");
        console.log("Sample customers:", customersResponse.data?.slice(0, 3));
        setCustomers(customersResponse.data);
      } else {
        throw new Error(customersResponse.message || "Failed to fetch customers");
      }

      if (groupsResponse.success) {
        console.log("Customer groups loaded:", groupsResponse.data);
        setCustomerGroups(groupsResponse.data);
      } else {
        console.warn("Failed to fetch customer groups:", groupsResponse.message);
        setCustomerGroups([]);
      }

      if (stationsResponse.success) {
        setStationOptions(stationsResponse.data || []);
      } else {
        console.warn("Failed to fetch stations:", stationsResponse.message);
        setStationOptions([]);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Debounced live count of recipients matching Group + Station + Last Activity,
  // resolved server-side (station/activity can't be filtered from the in-memory
  // customer list — station is derived from Route data and activity from Package).
  useEffect(() => {
    const hasStationOrActivity = selectedStations.length > 0 || Boolean(activityPeriod);
    if (!hasStationOrActivity && !selectedGroupId) {
      setFilteredRecipients(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setCountLoading(true);
      try {
        const response = await GetBulkRecipients({
          groupId: selectedGroupId,
          station: selectedStations,
          activityMode,
          activityPeriod
        });
        setFilteredRecipients(response.success ? response.data : []);
      } catch (err) {
        console.error("Error counting filtered recipients:", err);
        setFilteredRecipients([]);
      } finally {
        setCountLoading(false);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [selectedGroupId, selectedStations, activityMode, activityPeriod]);

  const handleAddAllFiltered = () => {
    if (!filteredRecipients || filteredRecipients.length === 0) return;
    setSelectedCustomers(prev => {
      const existingIds = new Set(prev.map(c => c._id));
      const toAdd = filteredRecipients.filter(c => !existingIds.has(c._id));
      return [...prev, ...toAdd];
    });
  };

  // Filter customers based on selected group and exclude already selected ones
  const availableCustomers = customers.filter(customer => {
    const matchesGroup = !selectedGroupId || 
      customer.groupId === selectedGroupId;
    
    const notSelected = !selectedCustomers.some(c => 
      c._id === customer._id
    );
    
    return matchesGroup && notSelected;
  });

  // Enhanced filter function for better search
  const filterOptions = (options, { inputValue }) => {
    if (!inputValue) return options;
    
    const searchTerm = inputValue.toLowerCase();
    return options.filter(customer => 
      customer.name.toLowerCase().includes(searchTerm) ||
      customer.whatsapp.includes(searchTerm) ||
      (customer.email && customer.email.toLowerCase().includes(searchTerm))
    );
  };

  const handleCustomerSelect = (event, newValue) => {
    if (newValue) {
      setSelectedCustomers(prev => [...prev, newValue]);
      setAutocompleteValue(null);
      setInputValue(""); // Clear the search input field
    }
  };

  const handleCustomerRemove = (customerToRemove) => {
    setSelectedCustomers(prev => 
      prev.filter(c => c._id !== customerToRemove._id)
    );
  };

  const handleGroupFilter = (event) => {
    const value = event.target.value;
    console.log("Group filter changed to:", value === "" ? "All Groups" : value);
    setSelectedGroupId(value);
    setAutocompleteValue(null);
    setInputValue(""); // Clear search when group filter changes
  };

  const handleClearAll = () => {
    setSelectedCustomers([]);
    setSelectedSearchTerm("");
  };

  const visibleSelectedCustomers = selectedCustomers.filter(customer => {
    if (!selectedSearchTerm) return true;
    const term = selectedSearchTerm.toLowerCase();
    return (
      customer.name.toLowerCase().includes(term) ||
      customer.whatsapp.includes(term)
    );
  });

  const getCustomerGroupName = (groupId) => {
    const group = customerGroups.find(g => g.id === groupId);
    return group ? group.name : "No Group";
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Customers for Bulk WhatsApp Message
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Group Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          Filter by Customer Group
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={selectedGroupId}
            onChange={handleGroupFilter}
            displayEmpty
            variant="outlined"
            size="small"
          >
            <MenuItem value="">
              <em>All Groups</em>
            </MenuItem>
            {customerGroups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Station Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          {t("whatsappBulk.stationFilterLabel")}
        </Typography>
        <Autocomplete
          multiple
          size="small"
          options={stationOptions}
          value={stationOptions.filter(s => selectedStations.includes(s.id))}
          onChange={(event, newValue) => setSelectedStations(newValue.map(s => s.id))}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedStations.length === 0 ? t("whatsappBulk.allStations") : ""}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.name}
                color="secondary"
                variant="outlined"
              />
            ))
          }
        />
      </Box>

      {/* Last Activity Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          {t("whatsappBulk.activityFilterLabel")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <ToggleButtonGroup
            exclusive
            color="secondary"
            size="small"
            value={activityMode}
            disabled={activityPeriod === "never"}
            onChange={(event, newValue) => newValue && setActivityMode(newValue)}
          >
            <ToggleButton value="active" sx={{ minWidth: 140 }}>{t("whatsappBulk.activeWithin")}</ToggleButton>
            <ToggleButton value="inactive" sx={{ minWidth: 140 }}>{t("whatsappBulk.inactiveSince")}</ToggleButton>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={activityPeriod}
              onChange={(event) => setActivityPeriod(event.target.value)}
              displayEmpty
              variant="outlined"
              size="small"
            >
              <MenuItem value="">
                <em>{t("whatsappBulk.noActivityFilter")}</em>
              </MenuItem>
              {ACTIVITY_PERIOD_OPTIONS.map((period) => (
                <MenuItem key={period} value={period}>
                  {t(`whatsappBulk.period${period === "never" ? "Never" : period}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Filtered Recipients Preview */}
      {filteredRecipients !== null && (
        <Box sx={{ mb: 3 }}>
          <Alert
            severity="info"
            action={
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<PlaylistAddIcon />}
                disabled={countLoading || filteredRecipients.length === 0}
                onClick={handleAddAllFiltered}
              >
                {t("whatsappBulk.addAllFiltered")}
              </Button>
            }
          >
            {countLoading ? (
              <>
                <CircularProgress size={14} sx={{ mr: 1 }} />
                {t("whatsappBulk.loadingCount")}
              </>
            ) : (
              t("whatsappBulk.foundRecipients", { count: filteredRecipients.length })
            )}
          </Alert>
        </Box>
      )}

      {/* Customer Autocomplete */}
      <Box sx={{ mb: 3 }}>
        <Autocomplete
          size="small"
          value={autocompleteValue}
          inputValue={inputValue}
          onInputChange={(event, value) => setInputValue(value)}
          onChange={handleCustomerSelect}
          options={availableCustomers}
          filterOptions={filterOptions}
          getOptionLabel={(option) => 
            `${option.name} (${option.whatsapp})`
          }
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search and select customers"
              placeholder="Type customer name, phone, or email..."
              fullWidth
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="body1">
                  {option.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.whatsapp} • {getCustomerGroupName(option.groupId)}
                </Typography>
              </Box>
            </Box>
          )}
          noOptionsText={
            availableCustomers.length === 0 && customers.length > 0 
              ? "All customers already selected" 
              : "No customers found"
          }
        />
      </Box>

      {/* Selected Customers Display */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1">
            Selected Customers ({selectedCustomers.length})
          </Typography>
          {selectedCustomers.length > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleClearAll}
              startIcon={<ClearIcon />}
            >
              Clear All
            </Button>
          )}
        </Box>

        {selectedCustomers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No customers selected. Use the autocomplete above to add customers.
          </Typography>
        ) : (
          <>
            <TextField
              value={selectedSearchTerm}
              onChange={(event) => setSelectedSearchTerm(event.target.value)}
              placeholder={t("whatsappBulk.searchSelectedPlaceholder")}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            />
            {visibleSelectedCustomers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("whatsappBulk.noMatchingSelected")}
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {visibleSelectedCustomers.map((customer) => (
                  <Chip
                    key={customer._id}
                    avatar={<Avatar><PersonIcon /></Avatar>}
                    label={`${customer.name} (${customer.whatsapp})`}
                    onDelete={() => handleCustomerRemove(customer)}
                    color="secondary"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            )}
          </>
        )}
      </Box>

      {/* Summary */}
      {selectedCustomers.length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: BG_PURPLE_TINT, borderRadius: 2 }}>
          <Typography variant="body2" color="secondary">
            Ready to send WhatsApp message to {selectedCustomers.length} customer{selectedCustomers.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CustomerAutocomplete;